import type {ReactElement} from 'react';
import {ButtonSize} from '@Components/button';
import {AuthSsoButton} from '@Components/auth-sso-button/auth-sso-button';
import {Text, TextSize} from '@Components/text';
import {openErrorModal} from '@Modals/error-modal';
import {clearModalHistoryEntry} from '@Components/modal-container';
import type {AjaxSuccessResponse} from '@Utils/ajax.util';
import {useAppleSignIn} from '@Hooks/login-signup/useAppleSignIn';

export interface AppleLoginProps {
  redirectUrl?: string;
  isSignup?: boolean;
  isConnect?: boolean;
  isDisconnect?: boolean;
  /** Forwarded to the shared SSO shell; the widescreen login page draws these larger. */
  size?: ButtonSize;
  /** The two-column reflow leaves room for the provider name alone, not the full call to action. */
  showProviderNameOnly?: boolean;

  onAppleSignInDone?(userId: string): void;
}

export function AppleLogin({showProviderNameOnly = false, size = ButtonSize.MEDIUM, ...props}: AppleLoginProps): ReactElement<any> | null {
  const {signInWithApple} = useAppleSignIn({redirectUrl: props.redirectUrl, onSignInDone: props.onAppleSignInDone});

  const getButtonText = (): string => {
    if (showProviderNameOnly) {
      return window.i18next.t('pmwjs_apple');
    }

    return props.isSignup ? window.i18next.t('pmwjs_sign_in_with_apple') : window.i18next.t('pmwjs_log_in_with_apple');
  };

  const disconnectAppleAccount = (): void => {
    clearModalHistoryEntry();
    void window.PMW.write(window.PMW.util.site_url('user/disconnectAppleAccount'), null, (response: AjaxSuccessResponse) => {
      if (response.status === 'success') {
        window.location.href = window.PMW.util.site_url('user/editprofile');
      } else {
        openErrorModal({message: window.i18next.t('pmwjs_error_processing_request_try_again')});
      }
    });
  };

  const onDisconnectAppleAccount = async (): Promise<void> => {
    const response = await window.PMW.readLocal('authenticate/isEmailAccountUser');
    if (!('isEmailAccountUser' in response) || !('is2FAEnabled' in response)) {
      openErrorModal({message: window.i18next.t('pmwjs_error_processing_request_try_again')});
      return;
    }

    if (response.isEmailAccountUser) {
      window.PMW.openPasswordVerificationModal({
        onSuccessCallback: onDisconnectPasswordVerification,
        check2FAStatus: true
      });
    } else if (response.is2FAEnabled) {
      window.PMW.openTwoFactorAuthenticationVerificationModal({
        onVerificationSuccessCallback: disconnectAppleAccount,
        onlyTOTPValid: true
      });
    } else {
      disconnectAppleAccount();
    }
  }

  const onDisconnectPasswordVerification = (is2FAPending = false): void => {
    if (is2FAPending) {
      window.PMW.openTwoFactorAuthenticationVerificationModal({
        onVerificationSuccessCallback: disconnectAppleAccount,
        onlyTOTPValid: true
      });
    } else {
      disconnectAppleAccount();
    }
  }

  const onConnectAppleAccount = async (): Promise<void> => {
    const response = await window.PMW.readLocal('authenticate/isEmailAccountUser');
    if (!('isEmailAccountUser' in response) || !('is2FAEnabled' in response)) {
      openErrorModal({message: window.i18next.t('pmwjs_error_processing_request_try_again')});
      return;
    }

    if (response.isEmailAccountUser) {
      window.PMW.openPasswordVerificationModal({
        onSuccessCallback: onConnectPasswordVerification,
        check2FAStatus: true
      });
    } else if (response.is2FAEnabled) {
      window.PMW.openTwoFactorAuthenticationVerificationModal({
        onVerificationSuccessCallback: signInWithApple,
        onlyTOTPValid: true
      });
    } else {
      void signInWithApple();
    }
  }

  const onConnectPasswordVerification = (is2FAPending = false): void => {
    if (is2FAPending) {
      window.PMW.openTwoFactorAuthenticationVerificationModal({
        onVerificationSuccessCallback: signInWithApple,
        onlyTOTPValid: true
      });
    } else {
      void signInWithApple();
    }
  }

  if (props.isDisconnect) {
    return (
      <Text
        val={window.i18next.t('pmwjs_disconnect')}
        size={TextSize.MD}
        onClick={(): void => {
          void onDisconnectAppleAccount();
        }}
      />
    );
  }

  if (props.isConnect) {
    return (
      <Text
        val={window.i18next.t('pmwjs_connect')}
        size={TextSize.MD}
        onClick={(): void => {
          void onConnectAppleAccount();
        }}
      />
    );
  }

  return <AuthSsoButton icon="icon-apple" onClick={signInWithApple} size={size} text={getButtonText()} />;
}
